// РЕФ.1: онбординг-модалка для новых игроков «Введи Сигнал чумбы».
//
// Показывается один раз сразу после auth-новички у которых нет referrer_id.
// Флаг устанавливается в main.tsx → rememberOnboarding() → localStorage.
// После «Принять» или «Никто меня не приглашал» флаг удаляется.

import { useEffect, useState } from 'react';
import { useTelegram } from '../telegram/useTelegram';
import { setReferrer } from '../api/client';
import { useToast } from './Toast';
import { useTr } from '../i18n/useTr';

const STORAGE_KEY = 'corps.needsReferrerOnboarding';

export function ReferrerOnboarding() {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const toast = useToast(s => s.show);
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') setOpen(true);
    } catch { /* ignore */ }
  }, []);

  if (!open) return null;

  const dismiss = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setOpen(false);
  };

  const onSubmit = async () => {
    const cleaned = code.trim().toUpperCase();
    if (cleaned.length < 2) {
      hapticError();
      toast(tr(
        'Введи Сигнал или нажми «никто меня не звал»',
        "Enter a Beacon or tap «nobody invited me»"
      ), 'amber');
      return;
    }
    hapticTap();
    setBusy(true);
    try {
      await setReferrer(cleaned);
      hapticSuccess();
      toast(tr('Сигнал принят. Добро пожаловать в Корпы.', 'Beacon accepted. Welcome to Corps.'), 'green', 4000);
      dismiss();
    } catch (e: any) {
      hapticError();
      const msg = String(e?.message || '');
      if (msg.includes('code_not_found')) toast(tr('Такого Сигнала нет', 'No such Beacon'), 'red');
      else if (msg.includes('self_referral')) toast(tr('Это твой Сигнал, чумба', 'That\'s your own Beacon, choom'), 'amber');
      else if (msg.includes('cycle_detected')) toast(tr('Этого чумбу ты и так пригласил', 'You already invited this choom'), 'amber');
      else if (msg.includes('already_has_referrer')) {
        toast(tr('У тебя уже есть пригласивший', 'You already have an inviter'), 'amber');
        dismiss();
      }
      else toast(tr(`Ошибка: ${msg || 'не получилось'}`, `Error: ${msg || 'failed'}`), 'red');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16
      }}
    >
      <div
        style={{
          background: 'var(--bg-2, #101018)',
          width: '100%', maxWidth: 380,
          boxShadow: 'inset 0 0 0 0.5px var(--neon-mg)',
          padding: 20,
          display: 'flex', flexDirection: 'column', gap: 14
        }}
      >
        <div style={{
          color: 'var(--neon-mg)',
          fontSize: 13, letterSpacing: '0.14em',
          fontWeight: 700, textAlign: 'center'
        }}>
          ► {tr('ВХОДЯЩИЙ СИГНАЛ', 'INCOMING BEACON')}
        </div>
        <div style={{ color: 'var(--t-md)', fontSize: 13, lineHeight: 1.5 }}>
          {isEn ? (
            <>Choom, did one of your own bring you to Corps?
              If yes — enter their <strong style={{ color: 'var(--t-hi)' }}>Beacon</strong>.
              You both get a reward. If not — skip, all good.</>
          ) : (
            <>Чумба, тебя позвал в Корпы кто-то из своих?
              Если да — введи его <strong style={{ color: 'var(--t-hi)' }}>Сигнал</strong>.
              Получите оба наградку. Если нет — пропусти, всё ок.</>
          )}
        </div>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={isEn ? 'e.g. XR4ZQK' : 'напр. XR4ZQK'}
          maxLength={16}
          autoCapitalize="characters"
          style={{
            padding: '12px 14px',
            background: 'var(--bg)',
            color: 'var(--neon-cy)',
            border: '0.5px solid rgba(160,170,200,0.3)',
            fontSize: 18, fontFamily: 'inherit',
            letterSpacing: 2, textAlign: 'center',
            outline: 'none'
          }}
        />
        <button
          onClick={onSubmit}
          disabled={busy}
          style={{
            padding: '12px',
            background: 'var(--neon-mg)',
            color: 'var(--bg)',
            border: 'none',
            fontSize: 12, fontWeight: 700,
            letterSpacing: 1, cursor: busy ? 'wait' : 'pointer',
            fontFamily: 'inherit',
            opacity: busy ? 0.6 : 1
          }}
        >{tr('ПРИНЯТЬ СИГНАЛ', 'ACCEPT BEACON')}</button>
        <button
          onClick={() => { hapticTap(); dismiss(); }}
          disabled={busy}
          style={{
            padding: '8px',
            background: 'transparent',
            color: 'var(--t-md)',
            border: '0.5px solid var(--t-md)',
            fontSize: 11, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: 1
          }}
        >{tr('НИКТО МЕНЯ НЕ ЗВАЛ', "NOBODY INVITED ME")}</button>
        <div style={{ fontSize: 10, color: 'var(--t-lo)', textAlign: 'center', marginTop: 4 }}>
          {tr(
            'Сигнал можно ввести только один раз и только сейчас.',
            'You can enter a Beacon only once and only now.'
          )}
        </div>
      </div>
    </div>
  );
}
